import { Box } from "@outschool/backpack";
import { Loading } from "@outschool/ui-legacy-component-library";
import { useOnScreen } from "@outschool/ui-utils";
import lodashFlatten from "lodash/flatten";
import React from "react";

export default function IncrementalLoader({
  rootMargin,
  children,
  delayBetweenLoads = 200,
  loadOnlyAtEndOfList = true,
  showLoading = false,
  preloadChildren = 0,
  loadIncrement = 1,
}: {
  /** Margin around the bottom of the last child that's compared with the screen viewport to load more children */
  rootMargin: string;
  children: React.ReactNode | React.ReactNode[];
  preloadChildren?: number;
  delayBetweenLoads?: number;
  loadOnlyAtEndOfList?: boolean;
  showLoading?: boolean;
  loadIncrement?: number;
}) {
  const childrenAsArray = Array.isArray(children)
    ? typeof Array.prototype.flat === "function"
      ? children.flat()
      : lodashFlatten(children)
    : [children];
  const numTotalChildren = childrenAsArray.length;

  const [numLoadedChildren, setNumLoadedChildren] =
    React.useState(preloadChildren);
  const [lastLoadedChildren, setLastLoadedChildren] = React.useState(
    Date.now() - delayBetweenLoads,
  );

  const [endOfListRef, setEndOfListRef] = React.useState<
    HTMLDivElement | undefined
  >(undefined);
  const isEndOfListOnScreen: boolean = useOnScreen(endOfListRef, false, {
    rootMargin,
  });

  React.useEffect(() => {
    if (
      (!loadOnlyAtEndOfList || isEndOfListOnScreen) &&
      numLoadedChildren < numTotalChildren
    ) {
      const timeUntilNextLoad = Math.max(
        -1,
        delayBetweenLoads - (Date.now() - lastLoadedChildren),
      );
      const timeoutId = setTimeout(() => {
        setNumLoadedChildren(numLoadedChildren + loadIncrement);
        setLastLoadedChildren(Date.now());
      }, timeUntilNextLoad);
      return () => {
        clearTimeout(timeoutId);
      };
    }
    return () => {};
  }, [
    loadIncrement,
    loadOnlyAtEndOfList,
    delayBetweenLoads,
    numTotalChildren,
    numLoadedChildren,
    lastLoadedChildren,
    isEndOfListOnScreen,
  ]);

  return (
    <>
      {childrenAsArray.slice(0, numLoadedChildren + 1).map((child, i) => (
        <React.Fragment key={i}>{child}</React.Fragment>
      ))}
      <Box ref={setEndOfListRef}>
        {showLoading && numLoadedChildren < numTotalChildren && <Loading />}
      </Box>
    </>
  );
}
